import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangleIcon, CreditCardIcon, MonitorOffIcon, ServerOffIcon, Undo2Icon } from "lucide-react";

import { deleteAccount } from "@/gen/nodebase/v1/account-AccountService_connectquery";
import { getBillingStatus } from "@/gen/nodebase/v1/billing-BillingService_connectquery";
import { WorkerAccess } from "@/gen/nodebase/v1/billing_pb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page";
import { AccountScope } from "@/components/scope";
import { errorText } from "@/lib/format";
import { endDeletedAccount, useSession } from "@/lib/session";

// The Web half of account deletion. People subscribe here, so they must be
// able to stop paying and leave here too, without installing an app.
//
// Session plus the typed word, as in the mobile apps — no emailed code. The
// server accepts exactly that from any session (apps/control-api/account.go
// DeleteAccount), so a code asked for by this page would stop nobody holding a
// stolen token, only the owner whose mail is late. Reasoning and trigger for
// a server-side step-up: docs/paid-worker-signup.md.
//
// What the page says before the button follows the Terms (§07) and the iOS
// screen (DeleteAccountView.swift); change one, check the others.

const CONFIRMATION = "DELETE";

export function Account() {
  const session = useSession();
  const email = session?.email ?? "";
  return (
    <>
      <PageHeader title="Account" scope={<AccountScope noun="Settings" />} />
      <div className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Email</CardTitle>
            <CardDescription>
              You sign in with a code sent to{" "}
              <span className="font-medium text-foreground">{email}</span>.
            </CardDescription>
          </CardHeader>
        </Card>
        <DeleteAccount email={email} />
      </div>
    </>
  );
}

function DeleteAccount({ email }: { email: string }) {
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState("");
  const billing = useQuery(getBillingStatus, {});
  const subscribed =
    billing.data?.access === WorkerAccess.SUBSCRIBED ||
    billing.data?.access === WorkerAccess.PAST_DUE;
  const remove = useMutation(deleteAccount, {
    onSuccess: () => {
      // Clear before the session goes: the cache is keyed by RPC, not by
      // account, and every query in it now answers for nobody.
      queryClient.clear();
      endDeletedAccount(email);
    },
  });
  const confirmed = typed === CONFIRMATION;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!confirmed || remove.isPending) return;
    // The same request is safe to send again after a failure: the server
    // stops billing, then removes Workers and records, and revokes the session
    // only once all of it succeeded.
    remove.mutate({ confirmation: CONFIRMATION });
  }

  return (
    // Inline rather than a utility class: Worker Home's committed stylesheet is
    // built from this same source tree, and a class used only here would change
    // a Worker release input for a Dash-only page.
    <Card style={{ borderColor: "color-mix(in srgb, var(--destructive) 40%, transparent)" }} data-testid="delete-account">
      <CardHeader>
        <CardTitle>Delete account</CardTitle>
        <CardDescription>
          Permanently delete this account and everything in it. Read what it
          removes before you continue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <ul className="space-y-3 text-sm" data-testid="delete-consequences">
          <Consequence icon={<ServerOffIcon className="size-4" />} title="Your Workers are shut down and erased">
            Files, sessions and everything else stored on them are deleted.
            Export anything you need first.
          </Consequence>
          <Consequence icon={<MonitorOffIcon className="size-4" />} title="Every device is signed out">
            Your computers, phones and routers lose access, and their tunnels stop.
          </Consequence>
          <Consequence icon={<CreditCardIcon className="size-4" />} title="Any paid plan ends">
            Subscriptions are cancelled and won't renew. Charges already made
            aren't refunded because of deletion, except where the law requires
            a refund.
          </Consequence>
          <Consequence icon={<Undo2Icon className="size-4" />} title="Nothing can be restored">
            Your account, Workspaces and Workers can't be recovered, by you or
            by us.
          </Consequence>
        </ul>

        {subscribed && (
          <div className="nb-alert nb-alert--warning space-y-2 text-sm" data-testid="delete-billing-note">
            <p className="font-medium">
              Your subscription{billing.data?.priceLabel ? ` (${billing.data.priceLabel})` : ""} stops
              when you delete your account
            </p>
            <p>
              It is cancelled immediately, not at the end of the current billing
              period, and you won't be charged again. The rest of the period
              you've paid for isn't refunded automatically.
            </p>
            <p>
              If this is your first subscription and your first payment was less
              than 14 days ago, you can have that payment refunded in full: email{" "}
              <a className="underline underline-offset-4" href="mailto:support@nodebase.com">support@nodebase.com</a>{" "}
              from {email} within those 14 days, before or after you delete.
            </p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Some records may be kept where the law requires, such as for security
          or fraud prevention. See the{" "}
          <a className="underline underline-offset-4" href="https://www.nodebase.com/privacy.html#retention" target="_blank" rel="noreferrer">Privacy Policy</a>{" "}
          and{" "}
          <a className="underline underline-offset-4" href="https://www.nodebase.com/terms.html#early-access" target="_blank" rel="noreferrer">Terms</a>.
        </p>

        <form className="space-y-3" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="delete-confirmation">Type DELETE to confirm</Label>
            <Input
              id="delete-confirmation"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="max-w-xs"
              disabled={remove.isPending}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            {typed !== "" && !confirmed && (
              <p className="text-xs text-muted-foreground">
                Type DELETE exactly, in capital letters.
              </p>
            )}
          </div>
          {remove.error != null && (
            <div role="alert" data-testid="delete-error" className="nb-alert nb-alert--danger flex items-start gap-2 text-sm">
              <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
              <div className="space-y-1">
                <p>
                  Deletion didn't finish, so your account is still here and you
                  are still signed in. Press Delete my account to try again.
                </p>
                <p className="text-xs opacity-70">{errorText(remove.error)}</p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="destructive" disabled={!confirmed || remove.isPending}>
              {remove.isPending ? "Deleting…" : "Delete my account"}
            </Button>
            {remove.isPending && (
              <span className="text-sm text-muted-foreground">
                This can take a minute. Keep this page open.
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Consequence({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground">{children}</p>
      </div>
    </li>
  );
}

// AccountDeleted stands alone, like the login card it leads to: the session
// has already ended, and there is nothing left to navigate.
export function AccountDeleted({ email, onDone }: { email: string; onDone: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm" data-testid="account-deleted">
        <CardHeader>
          <CardTitle>Account deleted</CardTitle>
          <CardDescription>
            <span className="font-medium text-foreground">{email}</span> and its
            Workers, devices and data were deleted. Any subscription on it was
            cancelled, and this browser is signed out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            onClick={() => {
              // Back to the root, so a later sign-in does not open on the
              // Account page of a different account.
              navigate("/", { replace: true });
              onDone();
            }}
          >
            Done
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
